import React, { forwardRef, useState } from 'react';
import { PropTypes } from '../../../utils/propTypes';
import styles from './Toggle.module.css';

/**
 * Toggle — a two-state (on/off) pressable button, reimplemented in Athena's
 * idiom (JSX + CSS Module + 2.0 tokens). Flat: values reference cascading
 * semantic / --ds2-* tokens, so it's mode-invariant (no [data-theme] overrides).
 * Controlled (`pressed` + `onPressedChange`) or uncontrolled (`defaultPressed`).
 * Preview-only for now.
 *
 *   variant: 'default' | 'outline'
 *   size:    'sm' | 'default' | 'lg'
 */

const VARIANT_CLASS = { default: styles.vDefault, outline: styles.vOutline };
const SIZE_CLASS = { sm: styles.sSm, default: styles.sDefault, lg: styles.sLg };

/** Compose the toggle class string (mirrors shadcn's cva helper; for ToggleGroup). */
export function toggleVariants({ variant = 'default', size = 'default', className = '' } = {}) {
  return [styles.toggle, VARIANT_CLASS[variant], SIZE_CLASS[size], className]
    .filter(Boolean)
    .join(' ');
}

const Toggle = forwardRef(
  (
    {
      pressed,
      defaultPressed = false,
      onPressedChange,
      variant = 'default',
      size = 'default',
      disabled = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const isControlled = pressed !== undefined;
    const [internal, setInternal] = useState(defaultPressed);
    const on = isControlled ? pressed : internal;

    const toggle = (e) => {
      if (disabled) return;
      props.onClick?.(e);
      const next = !on;
      if (!isControlled) setInternal(next);
      onPressedChange?.(next);
    };

    return (
      <button
        ref={ref}
        type="button"
        aria-pressed={on}
        data-state={on ? 'on' : 'off'}
        disabled={disabled}
        className={toggleVariants({ variant, size, className })}
        {...props}
        onClick={toggle}
      >
        {children}
      </button>
    );
  }
);

Toggle.displayName = 'Toggle';

Toggle.propTypes = {
  pressed: PropTypes.bool,
  defaultPressed: PropTypes.bool,
  onPressedChange: PropTypes.func,
  variant: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
};

export default Toggle;
export { Toggle };
